// PostHog analytics wrapper

import posthog from "posthog-js";

interface AnalyticsEventProps {
  [key: string]: string | number | boolean | null | undefined;
}

type UserType = "admin" | "customer" | "end_user" | "eval";

interface IdentifyProps {
  userId?: string | null;
  email?: string | null;
  orgId?: string | null;
  accountId?: string | null;
  customerId?: string | null;
  userType?: UserType | null;
}

type AnalyticsEventName =
  | "chat_started"
  | "message_sent"
  | "message_complete"
  | "stop_clicked"
  | "chat_container_mounted"
  | "profile_field_saved"
  | "onboarding_started"
  | "onboarding_profile_saved"
  | "jgs_profile_linked"
  | "jgs_profile_skipped"
  | "jgs_profile_link_failed"
  | "jgs_search_initiated"
  | "jgs_search_results"
  | "jgs_search_error"
  | "onboarding_completed"
  | "onboarding_suggestion_clicked"
  | "checklist_item_opened"
  | "checklist_action_clicked"
  | "checklist_step_toggled"
  | "outreach_email_sent"
  | "outreach_draft_saved"
  | "outreach_draft_deleted"
  | "pipeline_stage_changed"
  | "pipeline_school_removed"
  | "pipeline_note_saved"
  | "school_searched"
  | "school_pipeline_toggled"
  | "login_submitted"
  | "login_succeeded"
  | "login_failed"
  | "magic_link_requested"
  | "password_reset_requested"
  | "password_reset_completed"
  | "password_setup_completed"
  | "invite_accepted"
  | "invite_login_link_requested"
  | "coach_player_linked"
  | "coach_player_unlinked"
  | "coach_player_searched"
  | "page_viewed"
  | "session_started"
  | "chat_thread_resumed"
  | "school_detail_viewed"
  | "outreach_draft_started"
  | "outreach_draft_edited"
  | "logout"
  | "coach_player_selected"
  | "feature_tip_shown"
  | "feature_tip_dismissed"
  | "feature_tip_clicked"
  | "login_email_identified"
  | "schools_viewed"
  | "pipeline_viewed"
  | "outreach_list_viewed";

class Analytics {
  private initialized = false;
  private defaultProps: Record<string, string | number | boolean> = {};
  private userProps: IdentifyProps = {};
  private preInitQueue: Array<{ event: string; props: AnalyticsEventProps | undefined }> = [];

  private isDebug(): boolean {
    try {
      // Enable logs in dev or if user enables local flag
      const mode = import.meta.env.MODE || "";
      const flag = localStorage.getItem("recruit_ai_analytics_debug") === "1";
      return mode === "development" || flag;
    } catch {
      return false;
    }
  }

  private debug(...args: any[]) {
    if (!this.isDebug()) return;
    try {
      console.log("[analytics]", ...args);
    } catch {}
  }

  private isOptedOut(): boolean {
    try {
      return localStorage.getItem("recruit_ai_analytics_opt_out") === "1";
    } catch {
      return false;
    }
  }

  init(posthogKey: string, options?: { posthogHost?: string }) {
    if (this.initialized) return;
    if (this.isOptedOut()) return;
    if (!posthogKey) return;
    try {
      try {
        this.debug("initializing posthog SDK for identity/session-replay");
        posthog.init(posthogKey, {
          api_host: options?.posthogHost || "https://us.i.posthog.com",
          autocapture: false,
          capture_pageview: false,
        });
      } catch {
        // no-op — an SDK init failure must not strand custom events in the queue
      }

      // Mark initialized so queued events drain through the proxy
      this.initialized = true;
      this.debug("initialized; draining queue size", this.preInitQueue.length);
      this.drainQueue();
    } catch {
      // no-op
    }
  }

  setContext(context: { env?: string; appVersion?: string; route?: string }) {
    const next: Record<string, string> = {};
    if (typeof context.env === "string") next.env = context.env;
    if (typeof context.appVersion === "string") next.appVersion = context.appVersion;
    if (typeof context.route === "string") next.route = context.route;
    this.defaultProps = { ...this.defaultProps, ...next };
    this.debug("setContext", next);
  }

  identify(props: IdentifyProps) {
    if (!props) return;
    const cleanProps: IdentifyProps = {
      userId: props.userId ?? this.userProps.userId ?? null,
      email: props.email ?? this.userProps.email ?? null,
      orgId: props.orgId ?? this.userProps.orgId ?? null,
      accountId: props.accountId ?? this.userProps.accountId ?? null,
      customerId: props.customerId ?? this.userProps.customerId ?? null,
      userType: props.userType ?? this.userProps.userType ?? null,
    };
    this.userProps = cleanProps;

    this.debug("identify", {
      userId: cleanProps.userId,
      email: cleanProps.email,
      orgId: cleanProps.orgId,
      accountId: cleanProps.accountId,
      customerId: cleanProps.customerId,
      userType: cleanProps.userType,
    });

    if (this.isOptedOut()) return;

    try {
      if (cleanProps.userId) {
        const posthogProperties: Record<string, string> = {};
        if (cleanProps.email) posthogProperties.email = String(cleanProps.email);
        if (cleanProps.orgId) posthogProperties.orgId = String(cleanProps.orgId);
        if (cleanProps.accountId) posthogProperties.accountId = String(cleanProps.accountId);
        if (cleanProps.customerId) posthogProperties.customerId = String(cleanProps.customerId);
        if (cleanProps.userType) posthogProperties.userType = String(cleanProps.userType);
        posthog.identify(String(cleanProps.userId), posthogProperties);
      }

      const posthogGroupKey = cleanProps.orgId
        ? "orgId"
        : cleanProps.accountId
          ? "accountId"
          : cleanProps.customerId
            ? "customerId"
            : null;
      const posthogGroupVal = (cleanProps as any)[posthogGroupKey || ""];
      if (posthogGroupKey && posthogGroupVal) {
        posthog.group(posthogGroupKey, String(posthogGroupVal));
      }
    } catch {
      // no-op
    }
  }

  reset() {
    this.userProps = {};
    try {
      posthog.reset();
    } catch {}
  }

  getPosthogSessionId(): string | null {
    try {
      const id = posthog.get_session_id();
      return id || null;
    } catch {
      return null;
    }
  }

  getPosthogDistinctId(): string | null {
    try {
      const id = posthog.get_distinct_id();
      return id || null;
    } catch {
      return null;
    }
  }

  private sanitizeValue(value: any): any {
    if (value == null) return value;
    if (typeof value === "string") {
      return this.redactPII(value);
    }
    if (Array.isArray(value)) {
      return value.map(v => this.sanitizeValue(v));
    }
    if (typeof value === "object") {
      const out: Record<string, any> = {};
      Object.entries(value).forEach(([k, v]) => {
        out[k] = this.sanitizeValue(v);
      });
      return out;
    }
    return value;
  }

  track(event: string | AnalyticsEventName, props?: AnalyticsEventProps) {
    try {
      if (this.isOptedOut()) return;
      const payload: AnalyticsEventProps = {
        ...this.defaultProps,
        ...(this.userProps.customerId ? { customerId: this.userProps.customerId } : {}),
        ...props,
      };
      const sanitized = this.sanitizeValue(payload);

      if (!this.initialized) {
        this.debug("queue event (pre-init)", event, sanitized);
        this.preInitQueue.push({ event: String(event), props: sanitized });
      } else {
        this.debug("track (proxy)", event, sanitized);
        this.sendToProxy(String(event), sanitized || {});
      }
    } catch {
      // no-op
    }
  }

  private sendToProxy(event: string, properties: AnalyticsEventProps) {
    try {
      const apiBase = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
      const posthogDistinctId = this.getPosthogDistinctId();
      const posthogSessionId = this.getPosthogSessionId();
      const body = JSON.stringify({
        event,
        properties,
        posthogDistinctId,
        posthogSessionId,
      });
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (posthogDistinctId) headers["X-POSTHOG-DISTINCT-ID"] = posthogDistinctId;
      if (posthogSessionId) headers["X-POSTHOG-SESSION-ID"] = posthogSessionId;
      // Fire-and-forget — don't block UI
      fetch(`${apiBase}/analytics/track`, {
        method: "POST",
        headers,
        credentials: "include",
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // no-op
    }
  }

  private drainQueue() {
    if (!this.initialized || this.preInitQueue.length === 0) return;
    const queue = this.preInitQueue.slice();
    this.preInitQueue = [];
    for (const item of queue) {
      try {
        this.track(item.event, item.props);
      } catch {}
    }
  }

  redactPII(text: string): string {
    if (!text) return "";
    let out = String(text);
    // Emails
    out = out.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email]");
    // Phone numbers (simple patterns)
    out = out.replace(
      /\b(?:\+?\d{1,3}[-.\s]?)?(?:\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})\b/g,
      "[phone]"
    );
    // Long numeric sequences (4+ digits)
    out = out.replace(/\d{4,}/g, "[num]");
    // JWT-like tokens
    out = out.replace(/[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/g, "[token]");
    // Redact URL query values
    try {
      const url = new URL(out);
      url.search = url.search.replace(
        /([?&])([^=]+)=([^&]+)/g,
        (_m, p1, k) => `${p1}${k}=[redacted]`
      );
      out = url.toString();
    } catch {}
    return out;
  }
}

export const analytics = new Analytics();

// Typed chat event helpers
export function trackChatStarted(props: { threadId: string; hasJgs: boolean }) {
  analytics.track("chat_started", { threadId: props.threadId, hasJgs: !!props.hasJgs });
}

export function trackMessageSent(props: { length: number }) {
  analytics.track("message_sent", { length: props.length });
}

export function trackMessageComplete() {
  analytics.track("message_complete");
}

export function trackStopClicked(props: { threadId: string }) {
  analytics.track("stop_clicked", { threadId: props.threadId });
}

export function trackChatContainerMounted() {
  analytics.track("chat_container_mounted");
}

export function trackProfileFieldSaved(field: string) {
  analytics.track("profile_field_saved", { field });
}

export function trackOnboardingStarted() {
  analytics.track("onboarding_started");
}

export function trackOnboardingProfileSaved(props: { hasGpa: boolean; hasSat: boolean }) {
  analytics.track("onboarding_profile_saved", props);
}

export function trackJgsProfileLinked(props: {
  source: "onboarding" | "profile" | "chat";
  matchCount?: number;
}) {
  analytics.track("jgs_profile_linked", props);
}

export function trackJgsProfileSkipped(props: { source: "onboarding" | "profile" | "chat" }) {
  analytics.track("jgs_profile_skipped", props);
}

export function trackJgsSearchInitiated(props: {
  source: "onboarding" | "profile" | "chat";
  query: string;
}) {
  analytics.track("jgs_search_initiated", props);
}

export function trackJgsSearchResults(props: {
  source: "onboarding" | "profile" | "chat";
  query: string;
  resultCount: number;
}) {
  analytics.track("jgs_search_results", props);
}

export function trackJgsSearchError(props: {
  source: "onboarding" | "profile" | "chat";
  query: string;
}) {
  analytics.track("jgs_search_error", props);
}

export function trackJgsProfileLinkFailed(props: { source: "onboarding" | "profile" | "chat" }) {
  analytics.track("jgs_profile_link_failed", props);
}

export function trackOnboardingCompleted(props: {
  outcome: "linked" | "skipped";
  destination?: string;
}) {
  analytics.track("onboarding_completed", props);
}

export function trackOnboardingSuggestionClicked(props: { suggestion: string; isLinked: boolean }) {
  analytics.track("onboarding_suggestion_clicked", props);
}

export function trackChecklistItemOpened(props: {
  stepTitle: string;
  stepIndex: number;
  phase: string;
}) {
  analytics.track("checklist_item_opened", props);
}

export function trackChecklistActionClicked(props: {
  stepTitle: string;
  actionLabel: string;
  actionType: "prompt" | "navigate";
}) {
  analytics.track("checklist_action_clicked", props);
}

export function trackChecklistStepToggled(props: {
  stepTitle: string;
  stepIndex: number;
  completed: boolean;
}) {
  analytics.track("checklist_step_toggled", props);
}

export function trackOutreachEmailSent(props: { outreachId: string }) {
  analytics.track("outreach_email_sent", props);
}

export function trackOutreachDraftSaved(props: { outreachId: string }) {
  analytics.track("outreach_draft_saved", props);
}

export function trackOutreachDraftDeleted(props: { outreachId: string }) {
  analytics.track("outreach_draft_deleted", props);
}

// Pipeline events
export function trackPipelineStageChanged(props: {
  schoolName: string;
  fromStage: string;
  toStage: string;
}) {
  analytics.track("pipeline_stage_changed", props);
}

export function trackPipelineSchoolRemoved(props: { schoolName: string; stage: string }) {
  analytics.track("pipeline_school_removed", props);
}

export function trackPipelineNoteSaved(props: { schoolName: string }) {
  analytics.track("pipeline_note_saved", props);
}

// School search events
export function trackSchoolSearched(props: { query: string; resultCount: number }) {
  analytics.track("school_searched", props);
}

export function trackSchoolPipelineToggled(props: {
  schoolName: string;
  schoolId: string;
  added: boolean;
}) {
  analytics.track("school_pipeline_toggled", props);
}

// Auth events
export function trackLoginSubmitted(props: { method: "password" | "magic_link" }) {
  analytics.track("login_submitted", props);
}

export function trackLoginSucceeded(props: {
  role: string;
  type: "password" | "magic_link_login" | "magic_link_signup";
}) {
  analytics.track("login_succeeded", props);
}

export function trackLoginFailed() {
  analytics.track("login_failed");
}

export function trackMagicLinkRequested() {
  analytics.track("magic_link_requested");
}

export function trackPasswordResetRequested() {
  analytics.track("password_reset_requested");
}

export function trackPasswordResetCompleted() {
  analytics.track("password_reset_completed");
}

export function trackPasswordSetupCompleted() {
  analytics.track("password_setup_completed");
}

export function trackInviteAccepted() {
  analytics.track("invite_accepted");
}

export function trackInviteLoginLinkRequested() {
  analytics.track("invite_login_link_requested");
}

// Coach events
export function trackCoachPlayerLinked(props: { playerName: string }) {
  analytics.track("coach_player_linked", props);
}

export function trackCoachPlayerUnlinked(props: { playerName: string }) {
  analytics.track("coach_player_unlinked", props);
}

export function trackCoachPlayerSearched(props: { query: string; resultCount: number }) {
  analytics.track("coach_player_searched", props);
}

// Navigation & session events
/** `source` is the link's `?src=` (see `analytics/source.ts`), so a visit from an email counts. */
export function trackPageViewed(props: { route: string; source?: string }) {
  analytics.track("page_viewed", props);
}

export function trackSessionStarted() {
  analytics.track("session_started");
}

export function trackChatThreadResumed(props: { threadId: string }) {
  analytics.track("chat_thread_resumed", props);
}

export function trackSchoolDetailViewed(props: { schoolName: string; schoolId: string }) {
  analytics.track("school_detail_viewed", props);
}

export function trackOutreachDraftStarted(props: { outreachId: string; schoolName: string }) {
  analytics.track("outreach_draft_started", props);
}

export function trackOutreachDraftEdited(props: { outreachId: string }) {
  analytics.track("outreach_draft_edited", props);
}

export function trackLogout() {
  analytics.track("logout");
}

export function trackCoachPlayerSelected(props: { playerName: string; jgsProfileId: string }) {
  analytics.track("coach_player_selected", props);
}

// Feature tip events
export function trackFeatureTipShown(props: { tipId: string }) {
  analytics.track("feature_tip_shown", props);
}

export function trackFeatureTipDismissed(props: { tipId: string }) {
  analytics.track("feature_tip_dismissed", props);
}

export function trackFeatureTipClicked(props: { tipId: string; action: string }) {
  analytics.track("feature_tip_clicked", props);
}

// Login identify step
export function trackLoginEmailIdentified(props: { action: "password" | "magic_link" }) {
  analytics.track("login_email_identified", props);
}

// Page view events
export function trackSchoolsViewed() {
  analytics.track("schools_viewed");
}

export function trackPipelineViewed() {
  analytics.track("pipeline_viewed");
}

export function trackOutreachListViewed() {
  analytics.track("outreach_list_viewed");
}

export function trackNotificationsViewed() {
  analytics.track("notifications_viewed");
}

/**
 * An action on School Watch news. `surface` is where it happened: the
 * Notifications page or a school's Recent news card. `source` is the page's
 * `?src=` when the visit came from an email.
 */
export function trackSchoolNewsAction(props: {
  action: "draft" | "add_fit" | "load_more" | "filter" | "read" | "show_more";
  type?: string;
  surface: "notifications" | "school";
  teamId?: string;
  source?: string;
}) {
  analytics.track("school_news_action", props);
}

/** A school's Recent news card showed news, once per school opened. */
export function trackSchoolNewsCardViewed(props: {
  teamId: string;
  itemCount: number;
  source?: string;
}) {
  analytics.track("school_news_card_viewed", props);
}
